import { normaliseIdentifier } from './search.js';
import { packageInfoParam } from './receipts.js';

/**
 * A package as the create-order flow needs to see it.
 *
 * Only what Step 1 renders: enough to recognise a parcel and decide whether it belongs on this
 * order. The full package record — warehouse, goods type, services, storage clock — belongs to the
 * Packages workstream, and pulling it in here would couple the order flow to fields it never uses.
 *
 * MYP-53 wires `order_list` and friends onto this; nothing here is a wire shape.
 */
export interface OrderablePackage {
  readonly id: string;
  /** The courier's tracking number, as the customer knows the parcel. */
  readonly trackingNumber: string;
  /**
   * What the customer declared is inside. Data, not copy — it arrives as the customer typed it,
   * often bilingually ("耳环 Earring"), and is rendered as received in every language.
   */
  readonly itemName: string;
  readonly quantity: number;
  /**
   * Weight in whole grams, or undefined when the warehouse has not weighed it yet.
   *
   * Integer grams for the same reason money is integer cents: summing 2.4 + 0.3 + 5.6 in floats
   * gives 8.299999999999999, and the running total on this screen is one of the few numbers a
   * customer checks against their own arithmetic.
   */
  readonly weightGrams?: number;
  /**
   * Where the parcel is and what kind of goods it is — together, what decides which channels can
   * carry it (`neworder_getChannel` takes both, MYP-87). Undefined when the vendor has not recorded
   * one; `channelScopeFor` refuses to guess for such a package.
   */
  readonly warehouseId?: string;
  readonly goodsType?: string;
}

/** Total weight of a selection, in whole grams. Unweighed packages contribute nothing. */
export function totalWeightGrams(packages: readonly OrderablePackage[]): number {
  return packages.reduce((total, item) => total + (item.weightGrams ?? 0), 0);
}

/** Grams to kilograms, for display only. */
export function gramsToKg(grams: number): number {
  return grams / 1000;
}

/** How an order gets paid once the warehouse has priced it. */
export type PaymentMode = 'MANUAL' | 'AUTO';

/**
 * A shipping channel, as the picker sells it.
 *
 * There is deliberately no price on this type, and — since MYP-53 slice 2 — no volumetric divisor
 * either. No channel endpoint returns either figure: a forwarding order is quoted only after the
 * warehouse weighs and packs it, and `channels` (渠道列表) carries no divisor field of any kind, on
 * any of its 205 live rows. An earlier fixture invented one to make a "L x W x H / 6000" line work;
 * this type does not carry a number nothing on the wire measured.
 *
 * A channel is therefore chosen on how long it takes, how it travels, what it costs to carry
 * (`weightBasis`), and whatever the vendor's own notice says about it.
 */
export interface ShippingChannel {
  readonly id: string;
  /** Name as received; often bilingual, rendered in every language as it arrives. */
  readonly name: string;
  readonly nameNative?: string;
  /** Code from the TRANSPORT_TYPE dictionary — AIR, SEA, UNC. */
  readonly transportType: string;
  readonly transitDaysMin: number;
  readonly transitDaysMax: number;
  /**
   * `calcweightway` on the wire: WEIGHT, VOL or BAG — how the channel turns a parcel into a
   * chargeable figure. Rendered through the WEIGHT_BASIS dictionary; not resolved here, the same
   * reason `transportType` isn't.
   */
  readonly weightBasis: string;
  /**
   * The vendor's own free-text notice for this channel — restrictions, surcharges, handling terms
   * — in the reader's language, decoded and whitespace-cleaned. Not a closed set of codes: the
   * live `remark`/`remark_en` fields are prose, not a restriction dictionary, so there is nothing
   * here to key a chip off. Undefined when the vendor left it blank, which 14 of 205 channels do.
   */
  readonly notice?: string;
}

/**
 * An optional service the warehouse performs on an order — vacuum packing, reinforcement, photos.
 *
 * `feeCents` is the vendor's `fee`: the live rows carry both `fee` and `defaultfee`, and the
 * vendor's answer to MYP-80 (Q&A v2, question 5) is that the customer side uses `fee` while
 * `defaultfee` is a back-office value — so `defaultfee` is never read. There is no separate
 * "minimum fee" field on the wire, so this does not carry one either. `requiresConfirmation` marks
 * the ones the warehouse only prices after inspecting the goods.
 */
export interface OrderService {
  readonly id: string;
  /**
   * The warehouse that performs it (`warehouseid`, MYP-89). Services are per warehouse — 28 live
   * across 11 warehouses, no id shared between two — so a service is only offered for an order
   * shipping from its own warehouse. See `servicesForWarehouse`.
   */
  readonly warehouseId?: string;
  readonly name: string;
  readonly nameNative?: string;
  readonly description: string;
  readonly feeCents?: number;
  /** The currency of `feeCents` as the vendor writes it (`RM`); `formatMoney` normalises it. */
  readonly currency?: string;
  /** The service's picture (`img`, MYP-116): a site path or a full URL. See `vendorAssetUrl`. */
  readonly imageUrl?: string;
  /**
   * The vendor's `fee`, exactly as it was sent (e.g. `"225.00"`). Used for one thing only:
   * `orders_create` wants a price per chosen service in `OrderServicesfee` (MYP-91), and `fee` is
   * the price the vendor says customers see (MYP-80). Shown as `feeCents`; this copy is only for
   * sending back exactly as written.
   */
  readonly billingFee?: string;
  readonly requiresConfirmation?: boolean;
}

/**
 * One level of the destination picker: country, then province, then city.
 *
 * A tree rather than three flat lists, because the API exposes `country`, `province` and `city` as
 * dependent lookups and the UI has to clear its descendants whenever a parent changes.
 */
export interface DestinationNode {
  readonly code: string;
  readonly name: string;
  readonly children?: readonly DestinationNode[];
  /**
   * Countries only: false when the vendor lists no states for it (8 of 14 live on 2026-09-24 —
   * Japan, Brunei, UAE, Australia, Italy, America, UK, Korea). An order needs a state and a city,
   * so such a country cannot be delivered to through this form. Undefined means not known yet.
   */
  readonly available?: boolean;
  /** Countries only: how many digits a postcode has there (`min/max_postcode_digit`), when set. */
  readonly postcodeDigits?: { readonly min: number; readonly max: number };
  /** Cities only: the postcode ranges the vendor records for it (`zip1`–`zip2`, `zip3`–`zip4`). */
  readonly postcodeRanges?: readonly { readonly from: string; readonly to: string }[];
}

/** Finds a node by code among siblings, so a cascade can resolve its own selection. */
export function findDestination(
  nodes: readonly DestinationNode[],
  code: string | undefined,
): DestinationNode | undefined {
  if (!code) return undefined;
  return nodes.find((node) => node.code === code);
}

/** The four steps of the create-order flow, in order. */
export const CREATE_ORDER_STEPS = ['packages', 'channel', 'delivery', 'confirm'] as const;

export type CreateOrderStep = (typeof CREATE_ORDER_STEPS)[number];

/**
 * Everything the create-order flow has collected so far.
 *
 * One object rather than a state variable per field. The Confirm step has to summarise all of it
 * and jump back to whichever step owns a given value, which is awkward when the values live in
 * nine separate `useState` calls in the page above it.
 *
 * Receipt images are deliberately absent: they are `File` handles and object URLs with a lifetime
 * tied to the page, not data, and mixing them in here would make the draft unserialisable the day
 * someone wants to keep a half-finished order.
 */
export interface OrderDraft {
  readonly packageIds: readonly string[];
  readonly channelId?: string;
  readonly countryCode?: string;
  readonly provinceCode?: string;
  readonly cityCode?: string;
  /**
   * Who receives the parcel and where in the chosen city. Always present as strings rather than
   * optional, so a controlled input never flips between uncontrolled and controlled; whether they
   * may be blank is `isStepComplete`'s business, not the type's.
   */
  readonly recipient: string;
  readonly phone: string;
  readonly line1: string;
  readonly postcode: string;
  readonly serviceIds: readonly string[];
  readonly paymentMode: PaymentMode;
  readonly couponCode: string;
  readonly remark: string;
}

export const EMPTY_ORDER_DRAFT: OrderDraft = {
  packageIds: [],
  recipient: '',
  phone: '',
  line1: '',
  postcode: '',
  serviceIds: [],
  paymentMode: 'MANUAL',
  couponCode: '',
  remark: '',
};

/**
 * Whether a step has everything it needs.
 *
 * Required is exactly what a courier needs to put the box in someone's hands: the destination, a
 * name to ask for, a number to call and a street to stand in. Services, coupon, remark and
 * postcode stay optional — several destinations here do not use postcodes, and refusing to
 * continue over an empty remark box would be inventing a rule the business does not have.
 *
 * Trimmed before testing, because a space is not a recipient.
 */
export function isStepComplete(draft: OrderDraft, step: CreateOrderStep): boolean {
  switch (step) {
    case 'packages':
      return draft.packageIds.length > 0;
    case 'channel':
      return draft.channelId !== undefined;
    case 'delivery':
      return Boolean(
        draft.countryCode &&
        draft.provinceCode &&
        draft.cityCode &&
        draft.recipient.trim() &&
        draft.phone.trim() &&
        draft.line1.trim(),
      );
    case 'confirm':
      return (
        isStepComplete(draft, 'packages') &&
        isStepComplete(draft, 'channel') &&
        isStepComplete(draft, 'delivery')
      );
  }
}

/**
 * The five stages an order passes through, in order.
 *
 * One list doing two jobs: the progress dots on an order card, and the filter chips above the
 * list. Keeping them as a single source is what stops a chip existing that no order can ever be
 * in — or a stage the filter cannot reach.
 */
export const ORDER_STAGES = [
  'collecting',
  'unpaid',
  'preparingDespatch',
  'shipped',
  'received',
] as const;

export type OrderStage = (typeof ORDER_STAGES)[number];

/**
 * Cancelled is not a stage — it is off the track entirely, not stalled somewhere along it.
 *
 * `exception` is the same idea for a different reason (MYP-53): the vendor publishes eighteen
 * order statuses, and three of them — 扣货 held, 问题订单 problem, 修改申请 modification requested —
 * describe an order that has stopped rather than one at a position. Rendering a held order as an
 * ordinary in-progress row is the app quietly telling the customer something untrue. The other
 * fifteen map onto the five stages and `cancelled`; see `toOrderStatus` in orders-wire.ts.
 */
export type OrderStatus = OrderStage | 'cancelled' | 'exception';

/** Position along the five stages, or 0 for an order that left the track. */
export function orderStageIndex(status: OrderStatus): number {
  const index = ORDER_STAGES.indexOf(status as OrderStage);
  return index === -1 ? 0 : index + 1;
}

/**
 * Whether the order is waiting on the customer rather than the warehouse.
 *
 * Drives the coloured dot on the status chip. It is deliberately not a severity ramp: the two
 * states that light up are exactly the two that carry a primary button, so the colour means
 * "you have something to do here" and nothing else.
 */
export function isActionRequired(status: OrderStatus): boolean {
  return status === 'unpaid' || status === 'shipped';
}

export type OrderAction = 'pay' | 'confirmReceipt' | 'invoice' | 'track' | 'edit' | 'cancel';

const ACTIONS_BY_STATUS: Record<OrderStatus, readonly OrderAction[]> = {
  collecting: ['edit', 'cancel'],
  unpaid: ['pay', 'invoice', 'track', 'edit'],
  preparingDespatch: ['invoice', 'track'],
  shipped: ['confirmReceipt', 'invoice', 'track'],
  received: ['invoice', 'track'],
  cancelled: [],
  // Nothing this app can offer on a held, problem or modification-requested order: every one of
  // them is waiting on the warehouse or on support, and a button that cannot help is worse than
  // no button. The status label itself is what tells the customer to get in touch.
  exception: [],
};

/**
 * Which controls an order offers at its current stage.
 *
 * Cancelling disappears the moment the warehouse has collected and packed, which is why an unpaid
 * order shows no Cancel: the goods are already boxed. The card explains that rather than simply
 * omitting the button, because a control that silently vanishes reads as a bug.
 */
export function availableOrderActions(status: OrderStatus): readonly OrderAction[] {
  return ACTIONS_BY_STATUS[status];
}

/** One line of the fee breakdown. */
export interface FeeLine {
  /** Label as received; the schedule varies by channel. */
  readonly label: string;
  readonly amountCents: number;
  /**
   * The vendor's currency code for this line (`feecurrency`, e.g. `"RM"`), as sent — `formatMoney`
   * normalises it. Unset when the vendor gave none; show the order's `currency` then (MYP-22).
   */
  readonly currency?: string;
}

export function totalFeeCents(lines: readonly FeeLine[]): number {
  return lines.reduce((total, line) => total + line.amountCents, 0);
}

/**
 * Where an order is going.
 *
 * Names and codes are both kept, and they are not redundant. The names are what a courier reads
 * and what the detail panel renders. The codes are what the destination cascade selects, and
 * without them reopening an order for editing means turning "Johor" back into a code by searching
 * the tree — which is guesswork the moment two provinces share a city name.
 *
 * The codes stay optional because an order that arrives from the API may predate them; the names
 * are what every order must have. Recipient, phone and street are optional for the same reason,
 * not because an undeliverable address is acceptable — the create flow now requires them.
 */
export interface DeliveryAddress {
  readonly recipient?: string;
  readonly phone?: string;
  readonly line1?: string;
  readonly postcode?: string;
  readonly city: string;
  readonly province?: string;
  readonly country: string;
  readonly countryCode?: string;
  readonly provinceCode?: string;
  readonly cityCode?: string;
}

/**
 * The waybill, and whatever is known about its journey.
 *
 * Only the number is required. `order_list` carries the waybill (`airno`) and the channel that
 * issued it and nothing else — no endpoint in the vendor API returns carrier scans, which is what
 * MYP-52 is for. Making the other two optional is how an order says "despatched, here is the
 * number, no movement reported yet" instead of the app inventing a line of status text to fill a
 * required field.
 */
export interface OrderTracking {
  readonly trackingNumber: string;
  readonly carrier?: string;
  /** Free text from the carrier, rendered as received. Absent until MYP-52 connects a feed. */
  readonly latestUpdate?: string;
}

export type OrderEvent =
  'created' | 'weighedPacked' | 'paid' | 'despatched' | 'delivered' | 'cancelled';

export interface OrderTimelineEvent {
  readonly event: OrderEvent;
  /** ISO date. */
  readonly at: string;
}

/**
 * A package as an order remembers it.
 *
 * `packageId` is required rather than optional, and that is a deliberate constraint on whatever
 * wires `order_list` later. Editing an order means ticking its packages back onto a list, and a
 * line that cannot name which package it is either has to be matched on tracking number — a
 * lookup pretending to be an identity — or silently dropped when the edit is saved. If the real
 * endpoint turns out to carry no id, that is worth raising rather than papering over here.
 */
export interface OrderPackageLine {
  readonly packageId: string;
  readonly trackingNumber: string;
  readonly itemName: string;
  readonly weightGrams?: number;
  /** `pkg_warehouseid` / `pkg_goodstype` on the detail record — see `OrderablePackage`. */
  readonly warehouseId?: string;
  readonly goodsType?: string;
  /** Purchase receipts attached to this package (`pkgInfo[].imageurl`, MYP-96): site paths. */
  readonly receiptPaths?: readonly string[];
}

/**
 * An order that has been submitted, as the Orders list shows it.
 *
 * A snapshot rather than a set of references: the packages are copied in, not looked up, because
 * an order must not change its contents because the customer later edits a package elsewhere.
 */
export interface SubmittedOrder {
  readonly orderNumber: string;
  /** ISO timestamp of submission. */
  readonly createdAt: string;
  readonly status: OrderStatus;
  /**
   * The vendor's own code for this order's state, e.g. `'PARTPACK'` or `'HOLD'` — one of the
   * eighteen `order_status_list` publishes, not one of the seven `status` groups it maps into.
   * Kept so a row can say precisely where the order is even though the stages reason in coarser
   * terms: a part-paid order sits under `unpaid` but reads as 部分未付款.
   */
  readonly statusCode?: string;
  /**
   * The vendor's own label for `statusCode`, resolved to the reader's language from the
   * `status_en`/`status_cn` pair `order_status_list` supplies. Not run through `t()` — these are
   * the vendor's words for its own workflow, the same reasoning `Package.statusLabel` follows.
   */
  readonly statusLabel?: string;
  readonly packages: readonly OrderPackageLine[];
  readonly channelId?: string;
  /** Carrier the channel hands off to, e.g. "NinjaVan". */
  readonly carrier?: string;
  readonly totalWeightGrams: number;
  /**
   * What the order is billed on, once weighed.
   *
   * Separate from the actual weight because they differ: a 4.1 kg parcel can be charged at 4.5 kg
   * after the channel's volumetric divisor and rounding. Showing only one of them makes the fee
   * breakdown look like an error.
   */
  readonly chargeableWeightGrams?: number;
  readonly feeLines?: readonly FeeLine[];
  /**
   * What the vendor says the order costs, in cents — its own `totalfee`, not a sum of the lines
   * above.
   *
   * The two are not always the same number, and the vendor's is the one that gets charged. Its
   * total folds in charges that never appear in the itemised list: add-on services, and a pickup
   * or delivery charge that arrives as a bare amount with no label at all. On the test account 62
   * of 284 orders bill more than their own fee lines add up to, one of them RM 0.30 against no fee
   * lines whatsoever. Re-deriving the total here would quietly understate every one of them, and a
   * customer shown RM 236.95 and debited RM 238.95 is right to distrust the whole page.
   *
   * Undefined until the warehouse has priced the order — see `orderPayableCents`.
   */
  readonly billedTotalCents?: number;
  /**
   * The currency the order is billed in (`currency`, e.g. `"RM"`), as the vendor sent it —
   * `formatMoney` normalises it and falls back to RM on anything unusable. Money varies per row
   * (MYP-22); all 284 live orders on the test account are RM today, which is why nothing looked
   * wrong before this was read.
   */
  readonly currency?: string;
  /** The vendor's own row id. The only key `order_list` accepts to return a detail record. */
  readonly vendorOrderId?: string;
  /** The warehouse the order ships from (`warehouseid`), from the detail record only. */
  readonly warehouseId?: string;
  /**
   * The add-on services on the order as the order itself names them (`OrderService` rows: `sid` +
   * `servicename`). Detail record only. Kept because the catalog does not know every one: live,
   * 7 orders use services 7 and 8, which `order_services` no longer lists, and one uses a service
   * that now belongs to another warehouse — without these names they would render as nothing.
   */
  readonly orderedServices?: readonly { readonly id: string; readonly name: string }[];
  readonly address?: DeliveryAddress;
  readonly tracking?: OrderTracking;
  readonly timeline?: readonly OrderTimelineEvent[];
  readonly serviceIds: readonly string[];
  readonly paymentMode: PaymentMode;
  readonly couponCode: string;
  readonly remark: string;
  /**
   * The vendor facts `order_modify` must be sent back (MYP-86), read off the detail record only.
   *
   * Undefined on a summary row and on an order built in this session. Also undefined on a detail
   * record that lacks `ordertype`, which the endpoint requires: such an order cannot be saved, and
   * guessing the type would be worse than saying so.
   */
  readonly modifyBase?: OrderModifyBase;
}

/**
 * What `order_modify` needs besides the fields the edit form collects.
 *
 * The endpoint overwrites the whole recipient block, so everything the form never shows — the
 * company name, the landline, the self-pickup point, the sender — has to be sent back exactly as
 * the vendor has it, or a save of the remark would blank them. Captured live on 2026-09-24 from
 * four editable orders (ORDERED, OUT, PACK, PENDING): every one of these columns is present on the
 * detail record.
 */
export interface OrderModifyBase {
  /** The raw status the form was opened at. The vendor refuses a save when it has moved on. */
  readonly statusCode: string;
  /** `ordertype`, e.g. NORMAL. `ABPOST` orders cannot be modified at all. */
  readonly orderType: string;
  /** `revphoneno` as stored. `address.phone` falls back to the landline, so it is kept apart. */
  readonly phoneNo: string;
  /** `revtel`, sent back as `revtelno`. */
  readonly telNo: string;
  readonly companyName: string;
  /** `pickupsiteid`, sent back as `pks`. `"0"` is home delivery. */
  readonly pickupSiteId: string;
  readonly sender: string;
  /**
   * The package services on this order as `pid-sid` pairs — the format `PkgServices` takes. The
   * edit form never changes them, so they are sent unchanged as both the before and the after.
   */
  readonly packageServices: readonly string[];
}

export type OrderStatusFilter = OrderStatus | 'all';
export type OrderSort = 'newest' | 'oldest';

export interface OrderFilters {
  readonly search: string;
  readonly status: OrderStatusFilter;
  /** A channel id, or 'all'. */
  readonly channelId: string;
  readonly sort: OrderSort;
}

export const EMPTY_ORDER_FILTERS: OrderFilters = {
  search: '',
  status: 'all',
  channelId: 'all',
  sort: 'newest',
};

/** Whether anything is narrowing the list — drives whether a Reset control is worth showing. */
export function hasActiveFilters(filters: OrderFilters): boolean {
  return (
    filters.search.trim() !== '' ||
    filters.status !== 'all' ||
    filters.channelId !== 'all' ||
    filters.sort !== EMPTY_ORDER_FILTERS.sort
  );
}

/**
 * Whether an order answers to a search box.
 *
 * Identifiers and prose are matched differently on purpose. An order number is `240612-501822` on
 * screen and `240612501822` in someone's head, so identifiers compare with separators stripped.
 * Item names are matched literally — collapsing their spaces too would let a query span two words
 * that were never adjacent.
 */
export function matchesOrderSearch(order: SubmittedOrder, query: string): boolean {
  const trimmed = query.trim();
  if (trimmed === '') return true;

  const identifierQuery = normaliseIdentifier(trimmed);
  const identifiers = [
    order.orderNumber,
    order.tracking?.trackingNumber ?? '',
    order.carrier ?? '',
    ...order.packages.map((item) => item.trackingNumber),
  ];

  if (identifiers.some((value) => normaliseIdentifier(value).includes(identifierQuery))) {
    return true;
  }

  const prose = trimmed.toLowerCase();
  return order.packages.some((item) => item.itemName.toLowerCase().includes(prose));
}

function matchesChannel(order: SubmittedOrder, channelId: string): boolean {
  return channelId === 'all' || order.channelId === channelId;
}

/** Search and channel applied, sorted. Status is applied separately — see countOrdersByStatus. */
export function filterOrders(
  orders: readonly SubmittedOrder[],
  filters: OrderFilters,
): readonly SubmittedOrder[] {
  const matched = orders.filter(
    (order) =>
      matchesOrderSearch(order, filters.search) &&
      matchesChannel(order, filters.channelId) &&
      (filters.status === 'all' || order.status === filters.status),
  );

  // Copied before sorting: sort mutates, and the caller's array is the provider's own list.
  return [...matched].sort((a, b) => {
    const left = orderSortKey(a);
    const right = orderSortKey(b);
    const oldestFirst = left < right ? -1 : left > right ? 1 : 0;
    return filters.sort === 'oldest' ? oldestFirst : -oldestFirst;
  });
}

/**
 * A comparable stamp for sorting, falling back to the order number.
 *
 * `order_list` summary rows carry no creation date — only the detail record does (MYP-53) — so
 * subtracting two `Date.parse('')` results gave `NaN`, and a comparator that returns `NaN` leaves
 * the list in whatever order the engine happened to produce. Silent, and wrong on every row.
 *
 * The order number is the fallback because it already encodes the date: `YYMMDD-nnnnnn`, zero
 * padded, so expanding it to an ISO-shaped string puts it on the same scale as a real timestamp
 * and sorts a mixed list — a few rows carrying a detail date, most not — chronologically. The
 * sequence rides along after the date so two orders placed on one day keep a stable order.
 */
function orderSortKey(order: SubmittedOrder): string {
  if (!Number.isNaN(Date.parse(order.createdAt))) return order.createdAt;

  const match = /^(\d{2})(\d{2})(\d{2})-(\d+)$/.exec(order.orderNumber.trim());
  if (!match) return '';

  const [, year, month, day, sequence] = match;
  return `20${year}-${month}-${day}T00:00:00.000Z#${sequence}`;
}

/**
 * How many orders sit at each status, given the *other* filters.
 *
 * Deliberately ignores `filters.status`. Counted after the status filter, picking "Unpaid" would
 * leave every other chip reading zero, and a row of zeroes tells a reader nothing about where the
 * rest of their orders went. Respecting search and channel but not status is what makes the
 * numbers a map of what clicking elsewhere would find.
 */
export function countOrdersByStatus(
  orders: readonly SubmittedOrder[],
  filters: OrderFilters,
): Record<OrderStatusFilter, number> {
  const pool = orders.filter(
    (order) =>
      matchesOrderSearch(order, filters.search) && matchesChannel(order, filters.channelId),
  );

  const counts: Record<OrderStatusFilter, number> = {
    all: pool.length,
    collecting: 0,
    unpaid: 0,
    preparingDespatch: 0,
    shipped: 0,
    received: 0,
    cancelled: 0,
    exception: 0,
  };

  for (const order of pool) counts[order.status] += 1;
  return counts;
}

/**
 * Reads filters back out of a URL.
 *
 * Everything is validated against what actually exists, so a hand-edited or stale link degrades to
 * the default view instead of rendering an empty list under a status nothing can ever be in.
 */
export function parseOrderFilters(
  params: URLSearchParams,
  knownChannelIds: readonly string[],
): OrderFilters {
  const status = params.get('status') ?? '';
  const channelId = params.get('channel') ?? '';
  const sort = params.get('sort') ?? '';

  const isKnownStatus =
    status === 'cancelled' || status === 'exception' || ORDER_STAGES.includes(status as OrderStage);

  return {
    search: params.get('q') ?? '',
    status: isKnownStatus ? (status as OrderStatus) : 'all',
    channelId: knownChannelIds.includes(channelId) ? channelId : 'all',
    sort: sort === 'oldest' ? 'oldest' : 'newest',
  };
}

/* -------------------------------------------------------------------------------------------- *
 * Editing an existing order (MYP-48)
 *
 * Create runs draft → order. Editing has to run order → draft → order, and the return leg is the
 * one that can quietly destroy things: an order carries fees, tracking, a carrier and a timeline
 * that the four-step form never collected and must never overwrite. So the functions below are
 * split — one reads an order into a draft, one patches a draft back onto the order it came from.
 * Neither rebuilds an order from scratch, which is the whole point.
 * -------------------------------------------------------------------------------------------- */

/** Which parts of an order a customer may still change, given where it is in the warehouse. */
export interface OrderEditability {
  /** Adding or removing parcels, and attaching receipts. */
  readonly packages: boolean;
  readonly channel: boolean;
  /** Address, services, payment mode, coupon and remark move together. */
  readonly delivery: boolean;
}

/**
 * What is still editable at a given status.
 *
 * `collecting` is before the warehouse has touched anything, so everything is open. From `unpaid`
 * the parcels have been weighed, boxed and priced: the contents and the shipping method are now
 * facts about a physical box on a shelf, not fields on a form. Where it goes and what should be
 * done to it are still just instructions, so those stay open.
 *
 * Past that the order is not editable at all — `availableOrderActions` stops offering Edit — and
 * this returns all-false so a hand-typed URL cannot walk around that.
 */
export function orderEditability(status: OrderStatus): OrderEditability {
  switch (status) {
    case 'collecting':
      return { packages: true, channel: true, delivery: true };
    case 'unpaid':
      return { packages: false, channel: false, delivery: true };
    default:
      return { packages: false, channel: false, delivery: false };
  }
}

/** Whether an order can be opened in the edit flow at all. */
export function isOrderEditable(status: OrderStatus): boolean {
  const editable = orderEditability(status);
  return editable.packages || editable.channel || editable.delivery;
}

/**
 * The raw status codes `order_modify` accepts (API doc p.54). Anything else is refused by the
 * vendor, so the form must not open for it.
 */
export const MODIFIABLE_ORDER_STATUS_CODES: readonly string[] = [
  'NEW',
  'ORDERED',
  'COLLECTED',
  'PENDING',
  'PACK',
  'OUT',
];

/**
 * Codes at which the vendor may bill a modification fee (`ModOrderFee`, API doc p.54). The amount
 * is not published, so the screen can only say that one may apply.
 */
export const MODIFICATION_FEE_STATUS_CODES: readonly string[] = ['PACK', 'OUT'];

const NONE_EDITABLE: OrderEditability = { packages: false, channel: false, delivery: false };

/**
 * What is editable on this particular order: the stage rule, narrowed by the vendor's own.
 *
 * The stage groups are coarser than what `order_modify` accepts. `PARTPACK` sits under `unpaid`,
 * which leaves delivery open, but the vendor refuses it; and an unrecognised code falls into
 * `collecting`, which would open everything. So an order that carries a raw code must also carry
 * one of `MODIFIABLE_ORDER_STATUS_CODES`, and an `ABPOST` order is closed outright. An order with
 * no raw code (one built in this session) keeps the stage rule alone.
 */
export function orderEditabilityFor(order: SubmittedOrder): OrderEditability {
  const byStage = orderEditability(order.status);
  const code = order.statusCode?.trim().toUpperCase();
  if (code === undefined) return byStage;
  if (!MODIFIABLE_ORDER_STATUS_CODES.includes(code)) return NONE_EDITABLE;
  if (order.modifyBase?.orderType.trim().toUpperCase() === 'ABPOST') return NONE_EDITABLE;
  return byStage;
}

/** `isOrderEditable`, for an order rather than a stage — see `orderEditabilityFor`. */
export function isOrderEditableFor(order: SubmittedOrder): boolean {
  const editable = orderEditabilityFor(order);
  return editable.packages || editable.channel || editable.delivery;
}

/** Whether saving an edit at this order's status may add a modification fee. */
export function modificationMayBeCharged(order: SubmittedOrder): boolean {
  const code = order.statusCode?.trim().toUpperCase();
  return code !== undefined && MODIFICATION_FEE_STATUS_CODES.includes(code);
}

/** The form fields of `order_modify`, all as strings so nothing is left to the encoder. */
export type OrderModifyParams = Readonly<Record<string, string>>;

/**
 * An edited draft → the `order_modify` request for the order it was read from.
 *
 * Three rules, each there because the endpoint is not a PUT:
 *
 * - **Everything is sent.** The endpoint rewrites the recipient block wholesale, so each field is
 *   either the customer's edit or the vendor's own value — never absent. Fields the form does not
 *   show come from `modifyBase`.
 * - **Only what is editable at this status is taken from the draft**, the same guard
 *   `applyDraftToOrder` applies, so a hand-typed URL cannot repack a boxed order.
 * - **Lists go as a before and an after.** Packages as `addPkgId`/`delPkgId`, order services as
 *   `orgOrderService`/`OrderServices`: the vendor diffs them to bill. Package services are sent
 *   unchanged on both sides, because the form never touches them and a missing pair would read as
 *   "remove them all".
 *
 * An unchanged phone is sent back as the vendor stored it. `address.phone` falls back to the
 * landline when there is no mobile, and without this rule a remark edit would copy the landline
 * into the mobile column.
 *
 * Returns undefined for an order without `modifyBase` — nothing can be sent for it safely.
 */
export function toOrderModifyParams(
  order: SubmittedOrder,
  draft: OrderDraft,
): OrderModifyParams | undefined {
  const base = order.modifyBase;
  if (base === undefined) return undefined;

  const editable = orderEditabilityFor(order);
  const before = orderToDraft(order);
  const next: OrderDraft = {
    ...before,
    ...(editable.packages ? { packageIds: draft.packageIds } : {}),
    ...(editable.channel ? { channelId: draft.channelId } : {}),
    ...(editable.delivery
      ? {
          countryCode: draft.countryCode,
          provinceCode: draft.provinceCode,
          cityCode: draft.cityCode,
          recipient: draft.recipient,
          phone: draft.phone,
          line1: draft.line1,
          postcode: draft.postcode,
          serviceIds: draft.serviceIds,
          remark: draft.remark,
        }
      : {}),
  };

  const phoneUnchanged = next.phone.trim() === before.phone.trim();
  const added = next.packageIds.filter((id) => !before.packageIds.includes(id));
  const removed = before.packageIds.filter((id) => !next.packageIds.includes(id));

  return {
    orderno: order.orderNumber,
    status: base.statusCode,
    ordertype: base.orderType,
    channel: next.channelId ?? '',
    country: next.countryCode ?? '',
    province: next.provinceCode ?? '',
    city: next.cityCode ?? '',
    revname: next.recipient.trim(),
    revaddr: next.line1.trim(),
    revphoneno: phoneUnchanged ? base.phoneNo : next.phone.trim(),
    revtelno: base.telNo,
    revcompanyname: base.companyName,
    revzipcode: next.postcode.trim(),
    pks: base.pickupSiteId,
    sender: base.sender,
    remark: next.remark.trim(),
    addPkgId: added.join(','),
    delPkgId: removed.join(','),
    OrderServices: next.serviceIds.join(','),
    orgOrderService: before.serviceIds.join(','),
    PkgServices: base.packageServices.join(','),
    orgPkgService: base.packageServices.join(','),
  };
}

/**
 * The payment method a new order starts with. The customer can switch it on Step 3.
 *
 * MANUAL by default because it is the choice that cannot move money on its own: with AUTO the
 * vendor takes the order's total from the wallet as soon as the warehouse prices it, and which
 * service price is billed is still unconfirmed (MYP-80). AUTO is offered anyway, by decision
 * (28 September 2026), reversing MYP-91's MANUAL-only rule.
 */
export const NEW_ORDER_DEFAULT_PAYMENT_MODE: PaymentMode = 'MANUAL';

/** The form fields of `orders_create`, all as strings so nothing is left to the encoder. */
export type OrderCreateParams = Readonly<Record<string, string>>;

/**
 * A finished draft → the `orders_create` request (MYP-91).
 *
 * `warehouseId` is the one warehouse the selected packages are in — the scope MYP-87 settles
 * before any channel can be chosen. `services` is the catalog the chosen service ids are looked
 * up in, for their prices.
 *
 * What is sent, and what is deliberately not:
 *
 * - **`ordertype` is NORMAL.** The document names NORMAL and TRANS "for example" and says nothing
 *   of what TRANS is; a customer placing an ordinary order is NORMAL.
 * - **`payment` is the draft's own `paymentMode`**, MANUAL or AUTO, as chosen on Step 3.
 * - **`OrderServicesfee`** lists each chosen service's `fee`, in the same order as
 *   `OrderServices` (see `OrderService.billingFee`). It is optional in the document, so if any
 *   chosen service has no price it is left out whole rather than sent with a hole in it: a list
 *   one short would pair every later price with the wrong service.
 * - **`pks` is 0**, home delivery. Nothing lists the pickup points it could name.
 * - **`codeno` is the draft's promo code, when there is one** (MYP-95). It has already passed
 *   `assessPromoCode` on Step 3. No discount amount is sent: there is no billing weight yet, and
 *   the vendor applies the discount itself when it prices the order.
 * - **No `revtelno`, `revcompanyname`, `sender`, `MaxPrice` or `splitBagValue`.** The form has no
 *   field for them, all are optional, and the last two are undocumented beyond their names.
 *
 * Returns undefined for a draft that is not finished (no channel or destination): the endpoint
 * requires them, and the caller should never have let the customer submit.
 */
export function toOrderCreateParams(
  draft: OrderDraft,
  warehouseId: string,
  services: readonly OrderService[],
  receiptPaths: Readonly<Record<string, readonly string[]>> = {},
): OrderCreateParams | undefined {
  const { channelId, countryCode, provinceCode, cityCode } = draft;
  if (
    draft.packageIds.length === 0 ||
    channelId === undefined ||
    countryCode === undefined ||
    provinceCode === undefined ||
    cityCode === undefined
  ) {
    return undefined;
  }

  // Only the order's own warehouse's price (MYP-119): the same service id can be listed, at its
  // own price, under more than one warehouse, and another warehouse's price is never sent. A row
  // with no warehouse at all is the one fallback. No match leaves the service unpriced, so the
  // whole fee list is left out, as for any other unpriced service.
  const fees = draft.serviceIds.map(
    (id) =>
      (
        services.find((service) => service.id === id && service.warehouseId === warehouseId) ??
        services.find((service) => service.id === id && service.warehouseId === undefined)
      )?.billingFee,
  );
  const allPriced = fees.every((fee): fee is string => fee !== undefined);

  return {
    pkgId: draft.packageIds.join(','),
    ordertype: 'NORMAL',
    warehouseid: warehouseId,
    channel: channelId,
    country: countryCode,
    province: provinceCode,
    city: cityCode,
    revname: draft.recipient.trim(),
    revaddr: draft.line1.trim(),
    revphoneno: draft.phone.trim(),
    revzipcode: draft.postcode.trim(),
    pks: '0',
    OrderServices: draft.serviceIds.join(','),
    ...(draft.serviceIds.length > 0 && allPriced ? { OrderServicesfee: fees.join(',') } : {}),
    remark: draft.remark.trim(),
    payment: draft.paymentMode,
    ...(draft.couponCode.trim() !== '' ? { codeno: draft.couponCode.trim() } : {}),
    ...receiptsParam(draft, receiptPaths),
  };
}

/**
 * `packageinfo` (MYP-96): the order's purchase-receipt paths, `{"<pkgId>": [paths]}`, sent when
 * there are any. The vendor keeps them for Korea/Thailand/Japan/Taiwan orders and insured ones and
 * ignores the field otherwise, so Create order only hands paths in for those (`receiptsWanted`,
 * MYP-140) — nothing to change here if the vendor starts keeping them everywhere.
 */
function receiptsParam(
  draft: OrderDraft,
  receiptPaths: Readonly<Record<string, readonly string[]>>,
): { packageinfo?: string } {
  const value = packageInfoParam(draft.packageIds, receiptPaths);
  return value === undefined ? {} : { packageinfo: value };
}

/** Reads a submitted order back into the shape the four-step form works in. */
export function orderToDraft(order: SubmittedOrder): OrderDraft {
  return {
    packageIds: order.packages.map((line) => line.packageId),
    channelId: order.channelId,
    countryCode: order.address?.countryCode,
    provinceCode: order.address?.provinceCode,
    cityCode: order.address?.cityCode,
    recipient: order.address?.recipient ?? '',
    phone: order.address?.phone ?? '',
    line1: order.address?.line1 ?? '',
    postcode: order.address?.postcode ?? '',
    serviceIds: order.serviceIds,
    paymentMode: order.paymentMode,
    couponCode: order.couponCode,
    remark: order.remark,
  };
}

/** The destination names a draft's codes point at, or undefined if the cascade is incomplete. */
function resolveDestination(
  draft: OrderDraft,
  destinations: readonly DestinationNode[],
): Pick<DeliveryAddress, 'city' | 'province' | 'country'> | undefined {
  const country = findDestination(destinations, draft.countryCode);
  const province = findDestination(country?.children ?? [], draft.provinceCode);
  const city = findDestination(province?.children ?? [], draft.cityCode);

  if (!country || !city) return undefined;
  return { city: city.name, province: province?.name, country: country.name };
}

/**
 * Builds the address an order should carry, from a draft and the destination tree.
 *
 * Shared by submit and by save so a created order and an edited one cannot drift into different
 * shapes — which is exactly how "Delivery address: None" happened the first time.
 */
export function draftToAddress(
  draft: OrderDraft,
  destinations: readonly DestinationNode[],
): DeliveryAddress | undefined {
  const resolved = resolveDestination(draft, destinations);
  if (!resolved) return undefined;

  return {
    ...resolved,
    countryCode: draft.countryCode,
    provinceCode: draft.provinceCode,
    cityCode: draft.cityCode,
    recipient: draft.recipient.trim() || undefined,
    phone: draft.phone.trim() || undefined,
    line1: draft.line1.trim() || undefined,
    postcode: draft.postcode.trim() || undefined,
  };
}

/**
 * Folds an edited draft back onto the order it was read from.
 *
 * Two rules do the work. Only fields the customer can still change at this status are taken from
 * the draft, so a hand-typed URL cannot repack a boxed parcel. And everything the form never
 * collected — fees, tracking, carrier, timeline, the order number and its creation date — is
 * carried over untouched, because a save is an amendment, not a resubmission.
 *
 * `pool` is the packages available to choose from; the caller owns that list, so this stays a
 * pure function of its arguments.
 */
export function applyDraftToOrder(
  order: SubmittedOrder,
  draft: OrderDraft,
  pool: readonly OrderablePackage[],
  destinations: readonly DestinationNode[],
): SubmittedOrder {
  const editable = orderEditabilityFor(order);

  const packages = editable.packages
    ? pool
        .filter((item) => draft.packageIds.includes(item.id))
        .map((item) => ({
          packageId: item.id,
          trackingNumber: item.trackingNumber,
          itemName: item.itemName,
          weightGrams: item.weightGrams,
        }))
    : order.packages;

  return {
    ...order,
    packages,
    totalWeightGrams: editable.packages
      ? packages.reduce((total, line) => total + (line.weightGrams ?? 0), 0)
      : order.totalWeightGrams,
    channelId: editable.channel ? draft.channelId : order.channelId,
    /*
      A draft whose destination cannot be resolved against `destinations` yields no address — and
      that means "I could not build one", never "delete the one this order has". Falling through to
      the existing address is what stops an order that came from the vendor, whose country/province
      /city codes are the vendor's own, from losing its delivery address to an edit of its remark.
      A real destination change still resolves and still lands.
    */
    address: editable.delivery
      ? (draftToAddress(draft, destinations) ?? order.address)
      : order.address,
    serviceIds: editable.delivery ? draft.serviceIds : order.serviceIds,
    paymentMode: editable.delivery ? draft.paymentMode : order.paymentMode,
    couponCode: editable.delivery ? draft.couponCode : order.couponCode,
    remark: editable.delivery ? draft.remark : order.remark,
  };
}

/**
 * Moves a draft onto a newer copy of the order it was read from, keeping only the customer's edits.
 *
 * Used when a save finds the order changed at the warehouse since the form opened. Keeping the
 * draft as it was would be wrong in a quiet way: every field the customer never touched still
 * holds the *old* value, so it would now read as a change and be sent back — silently undoing
 * whatever the warehouse changed. So each field the customer left alone takes the new value, and
 * only the fields they actually edited keep theirs.
 *
 * "Edited" is judged the way `changedOrderFields` judges it: text trimmed, lists as sets.
 */
export function rebaseOrderDraft(
  opened: SubmittedOrder,
  current: SubmittedOrder,
  draft: OrderDraft,
): OrderDraft {
  const before = orderToDraft(opened);
  const next = orderToDraft(current);
  const text = (a: string, b: string) => a.trim() === b.trim();
  const keep = <K extends keyof OrderDraft>(key: K, same: boolean): OrderDraft[K] =>
    same ? next[key] : draft[key];

  const destinationSame =
    before.countryCode === draft.countryCode &&
    before.provinceCode === draft.provinceCode &&
    before.cityCode === draft.cityCode;

  return {
    packageIds: keep('packageIds', sameSet(before.packageIds, draft.packageIds)),
    channelId: keep('channelId', before.channelId === draft.channelId),
    // The cascade moves as one: a new province without its country would be no address at all.
    countryCode: keep('countryCode', destinationSame),
    provinceCode: keep('provinceCode', destinationSame),
    cityCode: keep('cityCode', destinationSame),
    recipient: keep('recipient', text(before.recipient, draft.recipient)),
    phone: keep('phone', text(before.phone, draft.phone)),
    line1: keep('line1', text(before.line1, draft.line1)),
    postcode: keep('postcode', text(before.postcode, draft.postcode)),
    serviceIds: keep('serviceIds', sameSet(before.serviceIds, draft.serviceIds)),
    paymentMode: keep('paymentMode', before.paymentMode === draft.paymentMode),
    couponCode: keep('couponCode', text(before.couponCode, draft.couponCode)),
    remark: keep('remark', text(before.remark, draft.remark)),
  };
}

/** The fields an edit can touch, as they appear in a change list. */
export const ORDER_EDIT_FIELDS = [
  'packages',
  'channel',
  'destination',
  'recipient',
  'phone',
  'line1',
  'postcode',
  'services',
  'paymentMode',
  'couponCode',
  'remark',
] as const;

export type OrderEditField = (typeof ORDER_EDIT_FIELDS)[number];

/** Order-insensitive comparison: ticking A then B is not a different choice from B then A. */
function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((value, index) => value === right[index]);
}

/**
 * Which fields a draft changes relative to the order it was read from.
 *
 * Drives both halves of the footer: Save is dead until this is non-empty, and Discard only stops
 * to ask when it is. It also feeds the review step, because "you changed the remark" is a more
 * useful thing to confirm than a second copy of the whole order.
 *
 * Text is trimmed before comparing — typing a space into an empty remark and deleting it again
 * should leave the order alone, not arm a Save button.
 */
export function changedOrderFields(
  order: SubmittedOrder,
  draft: OrderDraft,
): readonly OrderEditField[] {
  const before = orderToDraft(order);
  const editable = orderEditabilityFor(order);
  const changed: OrderEditField[] = [];

  if (editable.packages && !sameSet(before.packageIds, draft.packageIds)) changed.push('packages');
  if (editable.channel && before.channelId !== draft.channelId) changed.push('channel');

  if (!editable.delivery) return changed;

  const movedTo =
    before.countryCode !== draft.countryCode ||
    before.provinceCode !== draft.provinceCode ||
    before.cityCode !== draft.cityCode;
  if (movedTo) changed.push('destination');

  if (before.recipient.trim() !== draft.recipient.trim()) changed.push('recipient');
  if (before.phone.trim() !== draft.phone.trim()) changed.push('phone');
  if (before.line1.trim() !== draft.line1.trim()) changed.push('line1');
  if (before.postcode.trim() !== draft.postcode.trim()) changed.push('postcode');
  if (!sameSet(before.serviceIds, draft.serviceIds)) changed.push('services');
  if (before.paymentMode !== draft.paymentMode) changed.push('paymentMode');
  if (before.couponCode.trim() !== draft.couponCode.trim()) changed.push('couponCode');
  if (before.remark.trim() !== draft.remark.trim()) changed.push('remark');

  return changed;
}

/**
 * Whether a change would leave the order's priced fee lines out of date.
 *
 * Services and the coupon are what a fee schedule reads, so changing either makes the breakdown on
 * screen stale. This deliberately reports staleness rather than recomputing: pricing is MYP-22 and
 * on hold, and a total invented here would be the most convincing wrong number in the app. An
 * order with no fee lines yet has nothing to go stale.
 */
export function feesGoStale(order: SubmittedOrder, changed: readonly OrderEditField[]): boolean {
  if (!order.feeLines || order.feeLines.length === 0) return false;
  return changed.includes('services') || changed.includes('couponCode');
}

/**
 * Marks an order paid.
 *
 * A patch, like applyDraftToOrder: the fee lines, tracking and packages are facts about an order
 * that paying does not alter, and rebuilding the record here would be a way to lose them. Only
 * two things move — the stage, and the timeline gaining the event that explains why.
 *
 * Paying advances an order to preparingDespatch rather than a "paid" stage of its own, because
 * that is what the warehouse does next and ORDER_STAGES describes where a parcel is, not what the
 * customer last did. The `paid` timeline event carries the "what the customer did" half.
 *
 * Anything not awaiting payment is returned untouched, so a double-submitted dialog cannot stamp
 * a second paid event onto an order that has already moved on.
 *
 * `toAuto` is MYP-50's one-gesture top-up-and-pay: paying and switching the order onto automatic
 * payment happen together rather than as a separate edit, so a customer who was just short of
 * covering an order doesn't have to find the edit screen afterward to stop it happening again.
 * There's no separate event for the mode change — the `paymentMode` field itself is the record,
 * the same way every other edited field is.
 */
export function payOrder(
  order: SubmittedOrder,
  at: Date,
  options?: { toAuto?: boolean },
): SubmittedOrder {
  if (order.status !== 'unpaid') return order;

  return {
    ...order,
    status: 'preparingDespatch',
    paymentMode: options?.toAuto ? 'AUTO' : order.paymentMode,
    timeline: [...(order.timeline ?? []), { event: 'paid', at: at.toISOString() }],
  };
}

/** What an order costs to settle, or undefined while the warehouse has not priced it. */
export function orderPayableCents(order: SubmittedOrder): number | undefined {
  if (!order.feeLines || order.feeLines.length === 0) return undefined;
  // The vendor's own total wins over a sum of the lines, for the reason `billedTotalCents`
  // documents. An order still carrying lines but no total falls back to adding them up, which is
  // what every order created inside this app does.
  return order.billedTotalCents ?? totalFeeCents(order.feeLines);
}
